import * as React from 'react'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  customFieldInputType,
  stringValue,
  toggleCustomFieldChoice,
} from '@/lib/contacts/custom-field-values'
import type { JoinCustomFieldValue, PublicJoinCustomField } from '@/pages/PublicJoinFormPage.types'

export function FieldShell({
  id,
  label,
  required,
  error,
  children,
}: {
  id: string
  label: string
  required?: boolean
  error?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Label htmlFor={id}>{label}</Label>
        {required && <Badge variant="warning">Required</Badge>}
      </div>
      {children}
      {error && <p className="text-caption text-destructive">{error}</p>}
    </div>
  )
}

export function PublicCustomFieldControl({
  field,
  value,
  error,
  onChange,
}: {
  field: PublicJoinCustomField
  value: JoinCustomFieldValue | undefined
  error?: string
  onChange: (value: JoinCustomFieldValue) => void
}) {
  const id = `join-custom-field-${field.id}`

  if (field.field_type === 'checkbox') {
    return (
      <FieldShell id={id} label={field.label} required={field.required} error={error}>
        <div className="flex items-center gap-2">
          <Checkbox
            id={id}
            checked={value === true}
            aria-invalid={Boolean(error)}
            onCheckedChange={(checked) => onChange(checked === true)}
          />
          <Label htmlFor={id} className="text-muted-foreground">
            Yes
          </Label>
        </div>
      </FieldShell>
    )
  }

  if (field.field_type === 'single-select') {
    return (
      <FieldShell id={id} label={field.label} required={field.required} error={error}>
        <Select value={stringValue(value)} onValueChange={onChange}>
          <SelectTrigger id={id} aria-invalid={Boolean(error)}>
            <SelectValue placeholder="Select a value" />
          </SelectTrigger>
          <SelectContent>
            {field.choices.map((choice) => (
              <SelectItem key={choice} value={choice}>
                {choice}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldShell>
    )
  }

  if (field.field_type === 'multi-select') {
    return <MultiSelectCustomField field={field} value={value} error={error} onChange={onChange} />
  }

  if (field.field_type === 'multiline text') {
    return (
      <FieldShell id={id} label={field.label} required={field.required} error={error}>
        <Textarea
          id={id}
          value={stringValue(value)}
          aria-invalid={Boolean(error)}
          onChange={(event) => onChange(event.target.value)}
        />
      </FieldShell>
    )
  }

  return (
    <FieldShell id={id} label={field.label} required={field.required} error={error}>
      <Input
        id={id}
        type={customFieldInputType(field.field_type)}
        value={stringValue(value)}
        aria-invalid={Boolean(error)}
        onChange={(event) => onChange(event.target.value)}
      />
    </FieldShell>
  )
}

function MultiSelectCustomField({
  field,
  value,
  error,
  onChange,
}: {
  field: PublicJoinCustomField
  value: JoinCustomFieldValue | undefined
  error?: string
  onChange: (value: JoinCustomFieldValue) => void
}) {
  const id = `join-custom-field-${field.id}`
  const selected = Array.isArray(value) ? value : []

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-body-sm text-foreground">
        {field.label}
        {field.required && (
          <Badge variant="warning" className="ml-2">
            Required
          </Badge>
        )}
      </legend>
      {field.choices.map((choice) => (
        <CustomFieldChoice
          key={choice}
          id={`${id}-${choice.toLowerCase().replace(/\s+/g, '-')}`}
          choice={choice}
          checked={selected.includes(choice)}
          onCheckedChange={(checked) =>
            onChange(toggleCustomFieldChoice(selected, choice, checked))
          }
        />
      ))}
      {error && <p className="text-caption text-destructive">{error}</p>}
    </fieldset>
  )
}

function CustomFieldChoice({
  id,
  choice,
  checked,
  onCheckedChange,
}: {
  id: string
  choice: string
  checked: boolean
  onCheckedChange: (checked: boolean | 'indeterminate') => void
}) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={onCheckedChange} />
      <Label htmlFor={id} className="text-muted-foreground">
        {choice}
      </Label>
    </div>
  )
}
